import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  approveGameRuleGeneratedDocumentReview,
  getGameRuleGeneratedDocumentDetail,
  putGameRuleGeneratedDocumentFile,
} from "../services/gameRuleGeneratedDocumentsService";
import { exportCurrentDocumentAsUint8Array } from "../services/gameRuleWordService";
import type { GameRuleGeneratedDocumentDetail } from "../types/gameRuleGenerated";
import { formatDisplayDateTime, formatIsoDateOnly } from "../utils/displayDate";

type BusyAction = "save" | "complete" | null;

const statusLabel = (status: string): string => {
  const normalized = status.trim().toLowerCase();
  if (normalized === "review") return "Pending review";
  if (normalized === "approved") return "Approved";
  if (normalized === "draft") return "Draft";
  return status;
};

const exportActiveDocxBlob = async (): Promise<Blob> => {
  const bytes = await exportCurrentDocumentAsUint8Array();
  const copy = Uint8Array.from(bytes);
  return new Blob([copy], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
};

export const PrizeLogicGeneratedDocumentReviewPage = () => {
  const { documentId = "" } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<GameRuleGeneratedDocumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<BusyAction>(null);

  const loadDetail = useCallback(async () => {
    if (!documentId.trim()) {
      setError("Document id is missing.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const next = await getGameRuleGeneratedDocumentDetail(documentId);
      setDetail(next);
    } catch (e: unknown) {
      setDetail(null);
      setError(e instanceof Error ? e.message : "Failed to load document detail.");
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const canMutate = Boolean(detail?.canReview);

  const saveDocument = useCallback(async (): Promise<void> => {
    if (!documentId.trim()) {
      throw new Error("Document id is missing.");
    }
    const blob = await exportActiveDocxBlob();
    const fileName = detail?.fileName?.trim() || "document.docx";
    await putGameRuleGeneratedDocumentFile(documentId, blob, fileName);
  }, [detail?.fileName, documentId]);

  const onSave = useCallback(async () => {
    if (!canMutate || busy) return;
    setBusy("save");
    setError(null);
    setSaveMessage(null);
    try {
      await saveDocument();
      setSaveMessage("Document saved successfully.");
      await loadDetail();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not save the document.");
    } finally {
      setBusy(null);
    }
  }, [busy, canMutate, loadDetail, saveDocument]);

  const onComplete = useCallback(async () => {
    if (!canMutate || busy) return;
    setBusy("complete");
    setError(null);
    setSaveMessage(null);
    try {
      await saveDocument();
      await approveGameRuleGeneratedDocumentReview(documentId);
      const name = detail?.name?.trim() || "Document";
      navigate("/prizelogic/listing", {
        replace: true,
        state: {
          generateSuccessMessage: `"${name}" was approved and marked complete.`,
        },
      });
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : "Could not complete the review. Save the document and try again.",
      );
    } finally {
      setBusy(null);
    }
  }, [busy, canMutate, detail?.name, documentId, navigate, saveDocument]);

  const onCancel = useCallback(() => {
    navigate("/prizelogic/listing");
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading review…
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error ?? "Document not found."}
        </div>
        <Link to="/prizelogic/listing" className="text-sm font-medium text-primary hover:underline">
          Back to listing
        </Link>
      </div>
    );
  }

  const busyOverlayLabel =
    busy === "complete" ? "Saving and completing review…" : "Saving document…";

  return (
    <div className="relative space-y-4">
      {busy ? (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-background/85 backdrop-blur-[2px]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-3 rounded-lg border border-[var(--border)] bg-card px-8 py-6 shadow-lg">
            <Loader2 className="h-9 w-9 animate-spin text-primary" aria-hidden />
            <p className="text-sm font-medium text-foreground">{busyOverlayLabel}</p>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{detail.name}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Review the game rule in Word, then save or complete from this pane.
          </p>
        </div>
        <button
          type="button"
          disabled={busy !== null}
          className="text-sm font-medium text-primary hover:underline shrink-0 disabled:opacity-50"
          onClick={onCancel}
        >
          Back to listing
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      {saveMessage ? (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {saveMessage}
        </div>
      ) : null}
      {!canMutate ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {detail.reviewLockedMessage?.trim() ||
            (detail.status.trim().toLowerCase() === "approved"
              ? "This document is approved and read-only in the task pane."
              : "You can view this document, but only the assigned reviewer can save or complete it.")}
        </div>
      ) : null}

      <div className="rounded-lg border border-[var(--border)] bg-card px-4 py-3 space-y-2 text-sm">
        <div className="flex flex-wrap justify-between gap-2">
          <span className="text-muted-foreground">Status</span>
          <span className="font-medium text-foreground">{statusLabel(detail.status)}</span>
        </div>
        <div className="flex flex-wrap justify-between gap-2">
          <span className="text-muted-foreground">File</span>
          <span className="font-medium text-foreground break-all">{detail.fileName}</span>
        </div>
        <div className="flex flex-wrap justify-between gap-2">
          <span className="text-muted-foreground">Reviewer</span>
          <span className="font-medium text-foreground">{detail.reviewerEmail ?? "—"}</span>
        </div>
        <div className="flex flex-wrap justify-between gap-2">
          <span className="text-muted-foreground">Sections</span>
          <span className="font-medium text-foreground">{detail.sectionCount}</span>
        </div>
        <div className="flex flex-wrap justify-between gap-2">
          <span className="text-muted-foreground">Generated</span>
          <span className="font-medium text-foreground">
            {formatIsoDateOnly(detail.generatedAt)}
          </span>
        </div>
        {detail.reviewerNotes?.trim() ? (
          <div className="pt-1 border-t border-border">
            <p className="text-muted-foreground mb-1">Reviewer notes</p>
            <p className="text-foreground whitespace-pre-wrap">{detail.reviewerNotes}</p>
          </div>
        ) : null}
        {detail.sections.length > 0 ? (
          <div className="pt-1 border-t border-border">
            <p className="text-muted-foreground mb-1">Included sections</p>
            <ul className="list-disc pl-5 space-y-0.5 text-foreground">
              {detail.sections.map((section) => (
                <li key={section}>{section}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {detail.status.trim().toLowerCase() === "approved" ? (
          <div className="pt-1 border-t border-border space-y-2">
            <div className="flex flex-wrap justify-between gap-2">
              <span className="text-muted-foreground">Reviewed by</span>
              <span className="font-medium text-foreground">{detail.reviewedBy ?? "—"}</span>
            </div>
            <div className="flex flex-wrap justify-between gap-2">
              <span className="text-muted-foreground">Reviewed at</span>
              <span className="font-medium text-foreground">
                {formatDisplayDateTime(detail.reviewedAt)}
              </span>
            </div>
            {detail.reviewComment?.trim() ? (
              <div>
                <p className="text-muted-foreground mb-1">Review comment</p>
                <p className="text-foreground whitespace-pre-wrap">{detail.reviewComment}</p>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2 justify-end">
        <button
          type="button"
          disabled={busy !== null}
          className="rounded-lg border border-input bg-background px-4 py-2 text-sm font-semibold text-foreground hover:bg-accent disabled:opacity-50"
          onClick={onCancel}
        >
          Cancel
        </button>
        {canMutate ? (
          <>
            <button
              type="button"
              disabled={busy !== null}
              className="rounded-lg border border-input bg-background px-4 py-2 text-sm font-semibold text-primary hover:bg-accent disabled:opacity-50"
              onClick={() => void onSave()}
            >
              {busy === "save" ? "Saving…" : "Save Document"}
            </button>
            <button
              type="button"
              disabled={busy !== null}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              onClick={() => void onComplete()}
            >
              {busy === "complete" ? "Completing…" : "Complete"}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};
